import { useState } from 'react';
import type { RequisitionDetail, RequisitionStatus } from '#shared/types';
import { useMutation } from '#client/hooks/useMutation';
import { transitionRequisition } from './procurement.service';

/**
 * Where a move stands. A rejection first asks for its reason; a move in flight
 * remembers where it is going, and a rejection keeps its form open while it is.
 */
type MoveState =
  { kind: 'idle' } | { kind: 'askingReason' } | { kind: 'moving'; to: RequisitionStatus };

/**
 * Moving a requisition along the ladder, including the rejection that has to
 * ask for its reason before it can be sent.
 */
export function useRequisitionMove(requisition: RequisitionDetail, onMoved: () => void) {
  const transition = useMutation();
  const [state, setState] = useState<MoveState>({ kind: 'idle' });
  const [reason, setReason] = useState('');

  const pendingTo = state.kind === 'moving' ? state.to : null;
  const isRejecting =
    state.kind === 'askingReason' || (state.kind === 'moving' && state.to === 'rejected');
  // The move in progress, or the rejection waiting for its reason; every other
  // button is disabled while there is one.
  const activeTo = pendingTo ?? (isRejecting ? 'rejected' : null);

  async function move(to: RequisitionStatus) {
    if (to === 'rejected' && reason.trim().length === 0) {
      setState({ kind: 'askingReason' });
      return;
    }

    setState({ kind: 'moving', to });
    const outcome = await transition.run(() =>
      transitionRequisition(requisition.id, {
        to,
        expected: requisition.status,
        reason: to === 'rejected' ? reason : undefined,
      }),
    );
    if (!outcome.ok) {
      // A failed rejection keeps its form open, so a retry shows the reason it
      // is about to send rather than sending it from a form that has closed.
      setState(to === 'rejected' ? { kind: 'askingReason' } : { kind: 'idle' });
      return;
    }
    setState({ kind: 'idle' });
    setReason('');
    onMoved();
  }

  return {
    move,
    pendingTo,
    activeTo,
    isRejecting,
    reason,
    setReason,
    errorMessage: transition.errorMessage,
  };
}
